import { useQuery } from '@tanstack/react-query'

import type { SeatUsage } from '@repo/types'

import { createApiCall } from 'services'

/**
 * Seats the current company is using against what its plan sells — VRG-438.
 *
 * Reads the SAME computation the invite path enforces (`GET /companies/admin/me/seats`
 * and `InviteCompanyUserUseCase` both call `GetCompanySeatUsageUseCase`), rather than
 * deriving a count from the members list. Counting rows in the table would be a second,
 * subtly different definition — the list includes Vera staff and, with "Show archived"
 * on, archived members, neither of which occupies a seat — so the number on screen would
 * disagree with the number that refuses an invite.
 */
export const SEAT_USAGE_QUERY_KEY = ['companies', 'me', 'seats'] as const

export const useCompanySeats = (options?: { enabled?: boolean }) => {
  const query = useQuery({
    queryKey: SEAT_USAGE_QUERY_KEY,
    queryFn: async (): Promise<SeatUsage> => {
      const response = await createApiCall({
        method: 'GET',
        url: '/companies/admin/me/seats',
      })
      return response.data as SeatUsage
    },
    enabled: options?.enabled !== false,
    // Short, because an invite or a removal changes it and the header is on screen
    // while both happen. The mutations invalidate this key; the staleTime is only the
    // backstop for the paths that do not.
    staleTime: 1000 * 30,
  })

  return {
    seats: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  }
}

/**
 * "4 of 5 seats" / "Unlimited seats" — the header label.
 *
 * Short on purpose: it sits in a page header action group beside two buttons, so it has
 * to read at a glance and not push the actions off. The definition of what a seat IS
 * lives in the glossary and reaches the reader through the tooltip beside this (§44),
 * which is why this string does not try to explain itself.
 *
 * An over-limit company reads "7 of 5 seats" rather than being clamped. They are
 * grandfathered, and hiding the overage would make the blocked invite inexplicable.
 */
export const formatSeatUsage = (seats: SeatUsage): string =>
  seats.isUnlimited
    ? 'Unlimited seats'
    : `${seats.used} of ${seats.limit} seat${seats.limit === 1 ? '' : 's'}`
